import React, { useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
} from '@mui/material';
import ClockLoader from 'shared/components/ClockLoader';
import SearchableSelect from 'shared/components/SearchableSelect';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';
import {
  PERMISSION_OPTIONS,
  SERVICE_CODE_OPTIONS,
  comparePermissionsByActivity,
} from 'platform/rbac/Permissions';

const defaultInitial = {
  serviceCode: '',
  permission: '',
  description: '',
};

const validationSchema = yup.object({
  serviceCode: yup
    .string()
    .trim()
    .oneOf(
      SERVICE_CODE_OPTIONS.map((option) => option.value),
      'Select a valid service type'
    )
    .required('Service type is required'),
  permission: yup.string().trim().required('Permission key is required'),
  description: yup.string().trim(),
});

const CreatePermissionDialog = ({ open, onClose, onSuccess, createPermission }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitting(true);
    try {
      const permissionKey = values.permission.trim();
      const payload = {
        serviceCode: values.serviceCode.trim(),
        permission: permissionKey,
        description: values.description?.trim() || undefined,
        status: 'ACTIVE',
      };
      await createPermission(payload);
      toast.success('Permission created successfully');
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to create permission';
      toast.error(typeof err === 'string' ? err : 'Failed to create permission');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
        Add permission
      </DialogTitle>
      <Formik
        initialValues={defaultInitial}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} pt={1}>
                <SearchableSelect
                  name="serviceCode"
                  label="Service type"
                  value={values.serviceCode}
                  onBlur={handleBlur}
                  error={touched.serviceCode && Boolean(errors.serviceCode)}
                  onChange={(nextServiceCode) => {
                    const nextOptions = PERMISSION_OPTIONS.filter(
                      (option) => option.serviceCode === nextServiceCode
                    );
                    setFieldValue('serviceCode', nextServiceCode);
                    if (!nextOptions.some((option) => option.permission === values.permission)) {
                      setFieldValue('permission', nextOptions[0]?.permission ?? '');
                    }
                  }}
                  options={SERVICE_CODE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                />
                <SearchableSelect
                  name="permission"
                  label="Permission key"
                  value={values.permission}
                  onBlur={handleBlur}
                  error={touched.permission && Boolean(errors.permission)}
                  onChange={(nextPermission) => {
                    const selectedOption = PERMISSION_OPTIONS.find(
                      (option) => option.permission === nextPermission
                    );
                    setFieldValue('permission', nextPermission);
                    if (selectedOption?.serviceCode) {
                      setFieldValue('serviceCode', selectedOption.serviceCode);
                    }
                  }}
                  options={PERMISSION_OPTIONS
                    .filter((option) => !values.serviceCode || option.serviceCode === values.serviceCode)
                    .slice()
                    .sort((a, b) => comparePermissionsByActivity(a.permission, b.permission))
                    .map((option) => ({
                      value: option.permission,
                      label: option.label,
                      group: option.activity || 'Other',
                    }))}
                />
                <TextField
                  fullWidth
                  name="description"
                  label="Description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  multiline
                  minRows={2}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="secondary" disabled={submitting}>
                {submitting ? <ClockLoader inline size={22} /> : 'Create'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreatePermissionDialog;
